import { ProfileIcon } from '@design-systems/react-icons'
import AccountCard from './AccountCard'
import { Caption, Grid, Marker, Separator } from '@design-systems/canvas-ui'
import UserAvatar from 'users/components/UserAvatar'
import { useContext, useMemo } from 'react'
import { type Control } from 'react-hook-form'
import { useQuery } from '@apollo/client/react'
import { UserContext } from 'App/UserContext'
import ControlledFormField from 'components/form/ControlledFormField'
import {
  GET_ROLES,
  GetUserByIdResponse,
} from 'graphql/queries/users/queries/userQueries'
import { AccountSettingsInputs } from 'account/types'

type Props = {
  user: GetUserByIdResponse['getById']
  control: Control<AccountSettingsInputs>
}

export const ProfileInformationCard = ({ user, control }: Props) => {
  const { isInternal } = useContext(UserContext)

  const {
    data: rolesData,
    loading: rolesLoading,
    error: rolesError,
  } = useQuery(GET_ROLES, {
    skip: !isInternal,
  })

  const roleOptions = useMemo(() => {
    const roles = rolesData?.roles?.content ?? []
    return [
      { id: '', value: '', label: 'Select role' },
      ...roles.map((role) => ({
        id: role.id,
        value: role.id,
        label: role.name,
      })),
    ]
  }, [rolesData])

  return (
    <AccountCard
      svg={ProfileIcon}
      cardHeading="Profile information"
      cardSubheading="Basic information about your account."
    >
      <Grid.Container spacing="expanded">
        <Grid.Item size={{ xs: 12, sm: 6 }}>
          <Grid.Container>
            <Grid.Item size={{ xs: 12 }}>
              <Grid.Container align="center" spacing="dense">
                <Grid.Item>
                  <UserAvatar
                    firstName={user.firstName}
                    lastName={user.lastName}
                    lanId={user.lanId}
                  />
                </Grid.Item>
                <Grid.Item size={{ xs: true }}>
                  <strong className="hc8-fs-150">
                    {user.firstName} {user.lastName}
                  </strong>
                </Grid.Item>
              </Grid.Container>
            </Grid.Item>
            <Grid.Item size={{ xs: 12 }}>
              <Caption above="Company">{user.companyName}</Caption>
              <Separator />
            </Grid.Item>
            <Grid.Item size={{ xs: 12 }}>
              <Caption above="Email">{user.email}</Caption>
              <Separator />
            </Grid.Item>
            {isInternal && !rolesLoading && (
              <Grid.Item size={{ xs: 12 }}>
                <ControlledFormField
                  control={control}
                  type="select"
                  name="primaryRole"
                  id="primaryRole"
                  label="Role"
                  isDisabled={rolesLoading || rolesError}
                  options={roleOptions}
                />
              </Grid.Item>
            )}
          </Grid.Container>
        </Grid.Item>
        <Grid.Item size={{ xs: 12, sm: 6 }}>
          <Grid.Container>
            <Grid.Item size={{ xs: 12 }}>
              <Caption above="Status">
                <Marker color={user.active ? 'green' : 'red'}>
                  {user.active ? 'Active' : 'Inactive'}
                </Marker>
              </Caption>
            </Grid.Item>
            {!isInternal && (
              <Grid.Item size={{ xs: 12 }}>
                <Caption above="Current accreditations">
                  <Grid.Container spacing="dense">
                    {(user.accreditations ?? [])
                      .filter(
                        (accreditation) => accreditation.status === 'COMPLETED',
                      )
                      .map((accreditation) => (
                        <Grid.Item key={accreditation.code}>
                          <Marker color="purple">
                            {accreditation.description}
                          </Marker>
                        </Grid.Item>
                      ))}
                  </Grid.Container>
                </Caption>
              </Grid.Item>
            )}
          </Grid.Container>
        </Grid.Item>
      </Grid.Container>
    </AccountCard>
  )
}

export default ProfileInformationCard
